import { computed, type ComputedRef } from 'vue'

import type {
  DropdownableDimension,
  ScheduleTabSelection,
  WorkspaceSchedule,
} from '../models/schedule.types'
import { useReportScheduleStore } from '../stores/reportScheduleStore'

import { useWorkspace } from '@/features/account'

export interface UseReportSchedules {
  /** Selected-workspace count without exposing the mutable raw schedule collection. */
  scheduleCount: ComputedRef<number>
  getScheduleByReportId: (
    reportId: number,
    workspaceIds?: readonly (number | string)[],
  ) => WorkspaceSchedule | null
  checkScheduleTabs: (reportId: number, tabs: string | readonly string[]) => ScheduleTabSelection
  dropdownableDimensions: (
    scheduleId: number | string,
    field: string,
  ) => DropdownableDimension | null
  loadDropdownValues: (scheduleId: number | string, field: string) => Promise<string[]>
}

/** Report-metadata surface owned by module 08 (contract 08 §4/§9, ledger A13). */
export function useReportSchedules(): UseReportSchedules {
  const store = useReportScheduleStore()
  const workspace = useWorkspace()

  const selectedWorkspaceIds = (): readonly (number | string)[] => {
    const id = workspace.workspaceId.value
    return id === undefined ? [] : [id]
  }

  const getScheduleByReportId = (
    reportId: number,
    workspaceIds: readonly (number | string)[] = selectedWorkspaceIds(),
  ): WorkspaceSchedule | null => store.getScheduleByReportId(reportId, workspaceIds)

  const checkScheduleTabs = (
    reportId: number,
    tabs: string | readonly string[],
  ): ScheduleTabSelection => {
    const tabMap = store.getScheduleTabs(getScheduleByReportId(reportId))
    if (typeof tabs !== 'string') {
      const matches = tabs.flatMap((tab) => {
        const detail = tabMap[tab]
        return detail ? [detail] : []
      })
      return matches.length > 0 ? matches : null
    }
    return tabMap[tabs] || null
  }

  const loadDropdownValues = (scheduleId: number | string, field: string): Promise<string[]> => {
    const workspaceId = workspace.workspaceId.value
    return workspaceId === undefined
      ? Promise.resolve([])
      : store.loadDropdownValues(workspaceId, scheduleId, field)
  }

  return {
    scheduleCount: computed(() => store.schedulesForWorkspace(workspace.workspaceId.value).length),
    getScheduleByReportId,
    checkScheduleTabs,
    dropdownableDimensions: (scheduleId, field) =>
      store.getDropdownableDimension(scheduleId, field),
    loadDropdownValues,
  }
}
